import { AnimatePresence, motion } from "framer-motion";
import { Gift, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useCart, GIFT_BOX_PRICE } from "../context/CartContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useState } from "react";

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total, giftBox, setGiftBox, giftMessage, setGiftMessage } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, size: i.size, quantity: i.quantity })),
        gift_box: giftBox,
        gift_message: giftBox && giftMessage.trim() ? giftMessage.trim() : null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed", { description: formatApiError(e) });
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-l border-navy/10 bg-cream p-0 sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-navy/10 px-7 py-6">
          <SheetTitle className="flex items-baseline justify-between font-serif text-2xl font-medium tracking-wide text-navy">
            Your Selection
            <span className="text-xs font-sans font-semibold uppercase tracking-[0.3em] text-burgundy" data-testid="cart-drawer-count">
              {items.length} {items.length === 1 ? "piece" : "pieces"}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-7 py-6">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center" data-testid="cart-empty-state">
              <span className="font-serif text-3xl italic text-navy/40">Bare drawers, bare ankles.</span>
              <p className="max-w-[240px] text-sm text-navy/60">Your cart awaits its first stroke of elegance.</p>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-continue-browsing"
                className="mt-2 border border-navy px-7 py-3 text-[0.7rem] font-bold uppercase tracking-[0.28em] text-navy transition-colors duration-300 hover:bg-navy hover:text-cream"
              >
                Continue Browsing
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              <AnimatePresence initial={false}>
                {items.map(({ key, product, size, quantity }) => (
                  <motion.li
                    key={key}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 32 }}
                    className="flex gap-4"
                    data-testid={`cart-item-${product.slug}-${size.toLowerCase()}`}
                  >
                    <div className="h-24 w-20 shrink-0 overflow-hidden bg-white/5">
                      <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-1 flex-col justify-between py-0.5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-serif text-lg leading-tight text-navy">{product.name}</p>
                          <p className="mt-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-burgundy">
                            {product.category_name} · Size {size}
                          </p>
                        </div>
                        <button onClick={() => remove(key)} aria-label={`Remove ${product.name} size ${size}`} data-testid={`cart-remove-${product.slug}-${size.toLowerCase()}`} className="text-navy/40 transition-colors duration-200 hover:text-burgundy">
                          <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-navy/15">
                          <button onClick={() => setQty(key, quantity - 1)} aria-label="Decrease quantity" data-testid={`cart-minus-${product.slug}-${size.toLowerCase()}`} className="flex h-8 w-8 items-center justify-center text-navy transition-colors duration-200 hover:bg-navy hover:text-cream">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${product.slug}-${size.toLowerCase()}`}>{quantity}</span>
                          <button onClick={() => setQty(key, quantity + 1)} aria-label="Increase quantity" data-testid={`cart-plus-${product.slug}-${size.toLowerCase()}`} className="flex h-8 w-8 items-center justify-center text-navy transition-colors duration-200 hover:bg-navy hover:text-cream">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="font-serif text-lg text-navy">${(product.price * quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-navy/10 bg-white/5 px-7 py-6">
            <button
              onClick={() => setGiftBox(!giftBox)}
              data-testid="gift-box-toggle"
              className={`mb-5 flex w-full items-center justify-between border px-4 py-3.5 text-left transition-colors duration-300 ${
                giftBox ? "border-gold bg-gold/10" : "border-navy/15 hover:border-gold/60"
              }`}
            >
              <span className="flex items-center gap-3">
                <Gift className={`h-4 w-4 ${giftBox ? "text-gold" : "text-navy/50"}`} strokeWidth={1.6} />
                <span>
                  <span className="block text-[0.65rem] font-bold uppercase tracking-[0.22em] text-navy">Signature Gift Box</span>
                  <span className="mt-0.5 block text-[0.6rem] tracking-wide text-navy/50">Navy keepsake box, gold ribbon, wax seal</span>
                </span>
              </span>
              <span className={`text-[0.65rem] font-bold tracking-[0.14em] ${giftBox ? "text-gold" : "text-navy/60"}`}>
                {giftBox ? "ADDED" : `+ $${GIFT_BOX_PRICE.toFixed(2)}`}
              </span>
            </button>
            {giftBox && (
              <textarea
                value={giftMessage}
                onChange={(e) => setGiftMessage(e.target.value)}
                maxLength={400}
                rows={2}
                placeholder="A note for the box — “Happy birthday, step boldly…”"
                data-testid="gift-message-input"
                className="mb-5 w-full resize-none border border-gold/40 bg-gold/5 p-3.5 font-serif text-sm italic text-navy placeholder:text-navy/35 focus:border-gold focus:outline-none"
              />
            )}
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[0.7rem] font-bold uppercase tracking-[0.3em] text-navy/60">Subtotal</span>
              <span className="font-serif text-xl text-navy" data-testid="cart-subtotal">${total.toFixed(2)}</span>
            </div>
            {giftBox && (
              <div className="mb-1.5 flex items-baseline justify-between" data-testid="cart-gift-line">
                <span className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-navy/50">Gift Box</span>
                <span className="font-serif text-lg text-navy/80">${GIFT_BOX_PRICE.toFixed(2)}</span>
              </div>
            )}
            <div className="mb-4 flex items-baseline justify-between border-t border-navy/10 pt-3">
              <span className="text-[0.7rem] font-bold uppercase tracking-[0.3em] text-navy">Total</span>
              <span className="font-serif text-2xl text-gold" data-testid="cart-grand-total">${(total + (giftBox ? GIFT_BOX_PRICE : 0)).toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="checkout-button"
              className="group relative flex w-full items-center justify-center gap-3 overflow-hidden bg-navy py-4 text-[0.72rem] font-bold uppercase tracking-[0.3em] text-cream transition-colors duration-500 disabled:opacity-60"
            >
              <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative transition-colors duration-500 group-hover:text-[#0B101E]">
                {loading ? "Preparing your runway…" : "Add Elegance — Checkout"}
              </span>
              <ArrowRight className="relative h-4 w-4 transition-colors duration-500 group-hover:text-[#0B101E]" strokeWidth={1.8} />
            </button>
            <p className="mt-3 text-center text-[0.65rem] uppercase tracking-[0.22em] text-navy/45">Secure checkout · Complimentary shipping over $75</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
